import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import styles from './VerifyAccountCard.module.css';

/**
 * VerifyAccountCard — email verification pending card.
 *
 * Mirrors Flutter VerifyAccCard widget.
 * Shown after successful signup while waiting for email verification.
 *
 * Features:
 *   - Shows registered email
 *   - Countdown timer for resend (60 seconds)
 *   - Resend verification email button
 *   - Re-sign up button
 *   - Logout button
 *
 * Flutter source files:
 *   - presentation/web/auth/web_signup/widgets/verify_acc_card.dart
 *   - domain/core/constants/string_constants.dart (VerifyAccCardConstants)
 */

interface VerifyAccountCardProps {
  email: string;
  disableResendBtn: boolean;
  onResend: () => void;
  onReSignUp: () => void;
  onLogout: () => void;
  onTimerEnd: () => void;
  isLoading?: boolean;
}

export function VerifyAccountCard({
  email,
  disableResendBtn,
  onResend,
  onReSignUp,
  onLogout,
  onTimerEnd,
  isLoading = false,
}: VerifyAccountCardProps) {
  const { t } = useLocalization();
  const [secondsRemaining, setSecondsRemaining] = useState(60);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start countdown timer
  useEffect(() => {
    if (disableResendBtn) {
      setSecondsRemaining(60);
      timerRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            onTimerEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [disableResendBtn, onTimerEnd]);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `0${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }, []);

  return (
    <div className={styles.card}>
      <h2 className={styles.title}>
        {t('Verify Account')}
      </h2>

      <p className={styles.description}>
        {t('We have sent an account activation link to your Email ID ')}{' '}
        <strong>{email}.</strong>{' '}
        {t(' Please click on the link to activate your account.')}
      </p>

      <div className={styles.resendRow}>
        <span className={styles.resendLabel}>
          {t("Didn't receive the verification email?")}
        </span>
        {disableResendBtn && secondsRemaining > 0 && (
          <span className={styles.timer}>
            {formatTime(secondsRemaining)}
          </span>
        )}
      </div>

      <button
        type="button"
        className={styles.primaryBtn}
        onClick={onResend}
        disabled={disableResendBtn || isLoading}
      >
        {t('Resend Verification Email')}
      </button>

      <button
        type="button"
        className={styles.primaryBtn}
        onClick={onReSignUp}
        disabled={isLoading}
      >
        {t('Re-sign up')}
      </button>

      <div className={styles.divider}>
        <span className={styles.dividerLine} />
        <span className={styles.dividerText}>{t('or')}</span>
        <span className={styles.dividerLine} />
      </div>

      <button
        type="button"
        className={styles.outlineBtn}
        onClick={onLogout}
        disabled={isLoading}
      >
        {t('Log out')}
      </button>
    </div>
  );
}
